/**
 * Platform Component Registry
 * ───────────────────────────
 * Single source of truth for every component that ships a web and/or mobile
 * version. Consumers import components by name; this file handles platform
 * detection and lazy-loading transparently.
 *
 * Three helpers are exported:
 *
 *   platform(webFactory, mobileFactory)
 *     Both web and mobile implementations exist. Webpack can statically
 *     analyse both explicit import() calls and produce the correct chunks.
 *
 *   webOnly(webFactory)
 *     Only a web implementation exists. On mobile the component renders
 *     nothing (NullComponent). The layout should never surface it on mobile.
 *
 *   mobileOnly(mobileFactory)
 *     Only a mobile implementation exists. On web the component renders
 *     nothing (NullComponent).
 *
 * New feature-folder architecture:
 *   Components now live in feature folders: ComponentName/web/ComponentName.js
 *   All imports below now point to the new feature folder structure.
 */

import React from 'react';

const isMobile = Boolean(window.Capacitor?.isNativePlatform() || window.cordova || /Mobi|Android/i.test(navigator.userAgent));

const NULL_FACTORY = () => import('./NullComponent');
const UNSUPPORTED_ON_MOBILE_FACTORY = () => import('./UnsupportedOnMobile/UnsupportedOnMobile');

/** Component available on both platforms. */
const platform = (webFactory, mobileFactory) =>
  React.lazy(isMobile ? mobileFactory : webFactory);

/** Component that only exists in web/. Mobile gets a silent no-op.
 *  Use this for app-level screens that are never surfaced in page layouts. */
const webOnly = (webFactory) =>
  React.lazy(isMobile ? NULL_FACTORY : webFactory);

/** Component that only exists in web/ but IS surfaced in page layouts
 *  via the widget registry. On mobile we show a helpful "only on desktop"
 *  notice instead of rendering nothing, so the page isn't blank. */
const webWithMobileNotice = (webFactory) =>
  React.lazy(isMobile ? UNSUPPORTED_ON_MOBILE_FACTORY : webFactory);

/** Component that only exists in mobile/. Web gets a silent no-op. */
const mobileOnly = (mobileFactory) =>
  React.lazy(isMobile ? mobileFactory : NULL_FACTORY);

// Export helpers for feature folder index.js files
export { platform, webOnly, webWithMobileNotice, mobileOnly };


// ─── Data / SDO ──────────────────────────────────────────────────────────────

export const SDOReadView = platform(
  () => import('./SDOReadView/web/SDOReadView'),
  () => import('./SDOReadView/mobile/SDOReadView'),
);

export const SDOReactView = platform(
  () => import('./SDOReactView/web/SDOReactView'),
  () => import('./SDOReactView/mobile/SDOReactView'),
);

export const SDOJsonFormReactView = platform(
  () => import('./SDOJsonFormReactView/web/SDOJsonFormReactView'),
  () => import('./SDOJsonFormReactView/mobile/SDOJsonFormReactView'),
);

export const SDOApexReactView = platform(
  () => import('./SDOApexReactView/web/SDOApexReactView'),
  () => import('./SDOApexReactView/mobile/SDOApexReactView'),
);

/** Mobile only — no web equivalent exists. */
export const SDOFromElastic = mobileOnly(
  () => import('./SDOFromElastic/mobile/SDOFromElastic'),
);

/** Mobile only — no web equivalent exists. */
export const SDOFromLucene = mobileOnly(
  () => import('./SDOFromLucene/mobile/SDOFromLucene'),
);

/** Mobile only — no web equivalent exists. */
export const PrimarySDO = mobileOnly(
  () => import('./PrimarySDO/mobile/PrimarySDO'),
);


// ─── Lists & Data display ────────────────────────────────────────────────────

export const UserData = platform(
  () => import('./DataList/UserView/web/UserView'),
  () => import('./DataList/UserView/mobile/UserView'), 
);

export const DBGrid = platform(
  () => import('./DBGrid/web/DBGrid'),
  () => import('./DBGrid/mobile/DBGrid'),
);

export const DistributionList = platform(
  () => import('./DistributionList/web/DistributionList'),
  () => import('./DistributionList/mobile/DistributionList'),
);

export const CardInstance = platform(
  () => import('./CardInstance/web/CardInstance'),
  () => import('./CardInstance/mobile/CardInstance'),
);

export const TabItem = platform(
  () => import('./TabItem/web/TabItem'),
  () => import('./TabItem/mobile/TabItem'),
);

export const LocalSearch = platform(
  () => import('./LocalSearch/web/LocalSearch'),
  () => import('./LocalSearch/mobile/LocalSearch'),
);

/** Mobile only — no web equivalent exists. */
export const NewSeqComponent = mobileOnly(
  () => import('./NewSeqComponent/mobile/NewSeqComponent'),
);

/** Mobile only — no web equivalent exists. */
export const TextSearchLucene = mobileOnly(
  () => import('./TextSearchLucene/mobile/TextSearchLucene'),
);


// ─── Workflow / Process ──────────────────────────────────────────────────────

export const WorkflowView = platform(
  () => import('./Workflow/WorkflowView/web/WorkflowView'),
  () => import('./Workflow/WorkflowView/mobile/WorkflowView'),
);

export const ProcessViewParent = platform(
  () => import('./ProcessViewParent/web/ProcessViewParent'),
  () => import('./ProcessViewParent/mobile/ProcessViewParent'),
);

/** Mobile only — no web equivalent exists. */
export const ProcessReadView = mobileOnly(
  () => import('./ProcessReadView/mobile/ProcessReadView'),
);


// ─── Tasks ───────────────────────────────────────────────────────────────────

export const AIAdvisorHistory = platform(
  () => import('./AIAdvisorHistory/web/AIAdvisorHistory'),
  () => import('./AIAdvisorHistory/mobile/AIAdvisorHistory'),
);

export const TaskDashboard = platform(
  () => import('./TaskDashboard/web/TaskDashboard'),
  () => import('./TaskDashboard/mobile/TaskDashboard'),
);

export const TasksPage = platform(
  () => import('./TasksPage/web/TasksPage'),
  () => import('./TasksPage/mobile/TasksPage'),
);


// ─── Charts & Visualisation ──────────────────────────────────────────────────

export const PieChart = webWithMobileNotice(
  () => import('./PieChart/web/PieChart'),
);

export const BarChart = platform(
  () => import('./BarChart/web/BarChart'),
  () => import('./BarChart/mobile/BarChart'),
);
export const ListItem = platform(
  () => import('./ListItem/web/ListItem'),
  () => import('./ListItem/mobile/ListItem'),
);

/** Mobile only — no web equivalent exists. */
export const Bar = mobileOnly(
  () => import('./Bar/mobile/Bar'),
);

export const LocalityChart = platform(
  () => import('./LocalityChart/web/LocalityChart'),
  () => import('./LocalityChart/mobile/LocalityChart'),
);

/** Mobile only — no web equivalent exists. */
export const Timeline = mobileOnly(
  () => import('./TimelineView/mobile/TimelineView'),
);

export const Carousel = platform(
  () => import('./Carousel/web/Carousel'),
  () => import('./Carousel/mobile/Carousel'),
);

export const FinancialDashboard = platform(
  () => import('./dashboards/FinancialDashboard/web/FinancialDashboard'),
  () => import('./dashboards/FinancialDashboard/mobile/FinancialDashboard'),
);

export const CropDashboard = platform(
  () => import('./dashboards/CropDashboard/web/CropDashboard'),
  () => import('./dashboards/CropDashboard/mobile/CropDashboard'),
);

export const CreditDashboard = platform(
  () => import('./dashboards/CreditDashboard/web/CreditDashboard'),
  () => import('./dashboards/CreditDashboard/mobile/CreditDashboard'),
);

/** Web only — no mobile Map component exists. Shown in page layouts,
 *  so use the mobile-notice variant rather than silent no-op. */
export const MapView = webWithMobileNotice(
  () => import('./Map/web/Map'),
);


// ─── App-level screens (used in App.js) ──────────────────────────────────────

export const ApplicationSelector = platform(
  () => import('./ApplicationSelector/web/ApplicationSelector'),
  () => import('./ApplicationSelector/mobile/ApplicationSelector'),
);

export const DeleteAccount = platform(
  () => import('./DeleteAccount/web/DeleteAccount'),
  () => import('./DeleteAccount/mobile/DeleteAccount'),
);

export const LoadingScreen = platform(
  () => import('./LoadingScreen/web/LoadingScreen'),
  () => import('./LoadingScreen/mobile/LoadingScreen'),
);

export const Login = platform(
  () => import('./Login/web/Login'),
  () => import('./Login/mobile/Login'),
);

/** Mobile only — web uses a different error screen. */
export const AppError = mobileOnly(
  () => import('./Error/mobile/Error'),
);

/** Help Centre — web (FAQ accordion page) and mobile variants. */
export const Help = platform(
  () => import('./Help/web/Help'),
  () => import('./Help/mobile/Help'),
);

/** Mobile only — no web Notifications component exists. */
export const Notifications = mobileOnly(
  () => import('./Notifications/mobile/Notifications'),
);

/** Full notifications page — web and mobile versions. */
export const NotificationsPage = platform(
  () => import('./Notifications/web/NotificationsPage'),
  () => import('./Notifications/mobile/NotificationsPage'),
);

export const Settings = platform(
  () => import('./Settings/web/Settings'),
  () => import('./Settings/mobile/Settings'),
);

export const CommunitySettings = platform(
  () => import('./CommunitySettings/web/CommunitySettings'),
  () => import('./CommunitySettings/mobile/CommunitySettings'),
);

export const UserSettings = platform(
  () => import('./UserSettings/web/UserSettings'),
  () => import('./UserSettings/mobile/UserSettings'),
);

export const WorkspaceSettings = platform(
  () => import('./WorkspaceSettings/web/WorkspaceSettings'),
  () => import('./WorkspaceSettings/mobile/WorkspaceSettings'),
);

export const KPI = platform(
  () => import('./KPI/web/KPIWidget'),
  () => import('./KPI/mobile/KPI'),
);

export const Filter = webWithMobileNotice(
  () => import('./Filter/web/Filter'),
);

export const TabsFilter = webWithMobileNotice(
  () => import('./TabsFilter/web/TabsFilter'),
);

export const Grid = webWithMobileNotice(
  () => import('./Grid/web/Grid'),
);

export const ReactHeatMap = webWithMobileNotice(
  () => import('./ReactHeatMap/web/ReactHeatMap'),
);

export const TrendChart = webWithMobileNotice(
  () => import('./TrendChart/web/TrendChart'),
);

export const RankingChart = webWithMobileNotice(
  () => import('./RankingChart/web/RankingChart'),
);

export const AttentionFlags = webWithMobileNotice(
  () => import('./AttentionFlags/web/AttentionFlags'),
);

export const GroupedTable = webWithMobileNotice(
  () => import('./GroupedTable/web/GroupedTable'),
);

export const PageHeader = webWithMobileNotice(
  () => import('./PageHeader/web/PageHeader'),
);

export const PageWithBottomBar = platform(
  () => import('./PageWithBottomBar/web/PageWithBottomBar'),
  () => import('./PageWithBottomBar/mobile/PageWithBottomBar'),
);
